import { getCsfRoute } from 'components/CreatorStoreFront/helpers/getCsfRoute';
import getPageTypeFromPath from 'components/CreatorStoreFront/helpers/getPageTypeFromPath';
import getSiteIdFromLinkSynergy from 'services/api/creatorStoreFront/getSiteIdFromLinkSynergy';
import { CSF_PAGE_TYPES } from 'constants/actionTypes/creatorStoreFront';
import anaConsts from 'analytics/constants';
import Empty from 'constants/empty';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import Storage from 'utils/localStorage/Storage';
import Tables from 'utils/indexedDB/Tables';
import UUIDv4 from 'utils/UUID';
import dateUtils from 'utils/Date';
import cookieUtil from 'utils/Cookies';

// ──────────────────────────────────────────────────────────────────────────────
// Private u1-construction primitives — duplicated from `csfAttribution.js`.
//
// These are pure utility functions (no storage / Redux / network side-effects).
// We deliberately duplicate them here instead of importing from the legacy
// module to maintain strict isolation between Dynamic Commissioning and the
// legacy CSF attribution flow. Keeping these stable string-builders local
// avoids coupling new Dynamic Commissioning logic to the legacy attribution
// module.
//
// These are stable string-builders. The DC copy may **intentionally diverge**
// from legacy where DC needs corrected behavior — the legacy copy is left
// untouched per the DC ↔ Legacy isolation rule (DC is a strangler-fig
// replacement, not a refactor of legacy). Per-function JSDoc records any
// intentional divergence and its rationale. Removal of this duplication
// happens when legacy CSF attribution is decommissioned — not inline with
// feature work.
// ──────────────────────────────────────────────────────────────────────────────

const U1_COLLECTION_DETAILS_PAGE_NAME = 'collectiondetail';
const U1_POST_DETAILS_PAGE_NAME = 'postdetail';
const MOTOM_TRAFFIC_SOURCE_CODES = {
    FACEBOOK: 'f',
    INSTAGRAM: 'i',
    TIKTOK: 't',
    BEACONS: 'b',
    LINKTREE: 'l',
    YOUTUBE: 'y',
    SEARCH: 's',
    LANDING_PAGE: 'a',
    OTHER: 'o'
};
const VALID_MOTOM_TRAFFIC_SOURCE_CODES = new Set(Object.values(MOTOM_TRAFFIC_SOURCE_CODES));
const UNSUPPORTED_OM_MMC_SOURCES = new Set(['email', 'sms', 'paid', 'display', 'affiliate', 'aff']);
const OM_MMC_SOURCE_MAP = {
    facebook: MOTOM_TRAFFIC_SOURCE_CODES.FACEBOOK,
    fb: MOTOM_TRAFFIC_SOURCE_CODES.FACEBOOK,
    instagram: MOTOM_TRAFFIC_SOURCE_CODES.INSTAGRAM,
    ig: MOTOM_TRAFFIC_SOURCE_CODES.INSTAGRAM,
    tiktok: MOTOM_TRAFFIC_SOURCE_CODES.TIKTOK,
    tt: MOTOM_TRAFFIC_SOURCE_CODES.TIKTOK,
    bytedance: MOTOM_TRAFFIC_SOURCE_CODES.TIKTOK,
    beacons: MOTOM_TRAFFIC_SOURCE_CODES.BEACONS,
    linktree: MOTOM_TRAFFIC_SOURCE_CODES.LINKTREE,
    youtube: MOTOM_TRAFFIC_SOURCE_CODES.YOUTUBE,
    yt: MOTOM_TRAFFIC_SOURCE_CODES.YOUTUBE,
    search: MOTOM_TRAFFIC_SOURCE_CODES.SEARCH,
    landing: MOTOM_TRAFFIC_SOURCE_CODES.LANDING_PAGE
};

/* eslint-disable camelcase */
function constructU1Param({
    host_type, traffic_source, device, page_type, identifier_location = '', merchant_tag, storefront_tag
}) {
    const prefix = [host_type, traffic_source, device, page_type, identifier_location].join('-');

    return [prefix, merchant_tag, storefront_tag].filter(Boolean).join('_');
}
/* eslint-enable camelcase */

function getPageTypeForAttribution(section, identifier) {
    const CXXC = 'cxxc';
    const PXXC = 'pxxc';
    const XXXC = 'xxxc';

    if (identifier) {
        if (section === CSF_PAGE_TYPES.POSTS) {
            return PXXC;
        } else if (section === CSF_PAGE_TYPES.COLLECTIONS) {
            return CXXC;
        }
    }

    const pageTypeMap = {
        [CSF_PAGE_TYPES.FEATURED]: XXXC,
        [CSF_PAGE_TYPES.POSTS]: XXXC,
        [CSF_PAGE_TYPES.COLLECTIONS]: XXXC,
        [CSF_PAGE_TYPES.PRODUCTS]: XXXC,
        '': XXXC
    };

    return pageTypeMap[section] || XXXC;
}

function generatePageIdentifierLocation(subAffiliateId, section, identifier) {
    if (identifier) {
        if (section === CSF_PAGE_TYPES.COLLECTIONS) {
            return `${identifier}_${U1_COLLECTION_DETAILS_PAGE_NAME}`;
        } else if (section === CSF_PAGE_TYPES.POSTS) {
            return `${identifier}_${U1_POST_DETAILS_PAGE_NAME}`;
        }
    }

    const location = !section || section === '' || section === CSF_PAGE_TYPES.FEATURED ? 'main' : section;

    return `${subAffiliateId}_${location}`;
}

function normalizeTrafficSourceCode(source) {
    return VALID_MOTOM_TRAFFIC_SOURCE_CODES.has(source) ? source : MOTOM_TRAFFIC_SOURCE_CODES.OTHER;
}

function normalizeForMatch(value) {
    return String(value || '').toLowerCase();
}

function getNormalizedTokens(value) {
    return normalizeForMatch(value)
        .split(/[^a-z0-9]+/)
        .filter(Boolean);
}

function tokensContain(tokens, values) {
    return values.some(value => tokens.includes(value));
}

function isSearchEngineReferrer(referrer) {
    if (!referrer) {
        return false;
    }

    const searchEngines = ['google.', 'bing.', 'yahoo.', 'duckduckgo.', 'baidu.', 'yandex.', 'ask.', 'aol.'];
    const referrerLower = normalizeForMatch(referrer);

    return searchEngines.some(engine => referrerLower.includes(engine));
}

function isCreatorDirectoryReferrer(referrer) {
    return normalizeForMatch(referrer).includes('creators.sephora.com');
}

function getTrafficSourceFromMotomSignals({ referrer = '', userAgent = '' } = {}) {
    const referrerLower = normalizeForMatch(referrer);
    const userAgentLower = normalizeForMatch(userAgent);
    const combinedSignals = `${userAgentLower} ${referrerLower}`;

    if (combinedSignals.includes('facebook')) {
        return MOTOM_TRAFFIC_SOURCE_CODES.FACEBOOK;
    }

    if (combinedSignals.includes('instagram') || userAgentLower.includes('igwebview') || userAgentLower.includes('igloader')) {
        return MOTOM_TRAFFIC_SOURCE_CODES.INSTAGRAM;
    }

    if (
        userAgentLower.includes('bytedancewebview') ||
        userAgentLower.includes('tiktok') ||
        userAgentLower.includes('musically') ||
        userAgentLower.includes('bytedance') ||
        referrerLower.includes('bytedance') ||
        referrerLower.includes('tiktok')
    ) {
        return MOTOM_TRAFFIC_SOURCE_CODES.TIKTOK;
    }

    if (referrerLower.includes('beacons.ai')) {
        return MOTOM_TRAFFIC_SOURCE_CODES.BEACONS;
    }

    if (referrerLower.includes('linktr.ee')) {
        return MOTOM_TRAFFIC_SOURCE_CODES.LINKTREE;
    }

    if (referrerLower.includes('youtube.com') || referrerLower.includes('youtu.be')) {
        return MOTOM_TRAFFIC_SOURCE_CODES.YOUTUBE;
    }

    if (isSearchEngineReferrer(referrerLower)) {
        return MOTOM_TRAFFIC_SOURCE_CODES.SEARCH;
    }

    if (isCreatorDirectoryReferrer(referrerLower)) {
        return MOTOM_TRAFFIC_SOURCE_CODES.LANDING_PAGE;
    }

    return MOTOM_TRAFFIC_SOURCE_CODES.OTHER;
}

function getDetailedSocialSource(omMmc, referrer, userAgent) {
    const omMmcLower = normalizeForMatch(omMmc);
    const omMmcTokens = getNormalizedTokens(omMmc);

    if (omMmcLower.includes('facebook') || tokensContain(omMmcTokens, ['fb'])) {
        return MOTOM_TRAFFIC_SOURCE_CODES.FACEBOOK;
    }

    if (omMmcLower.includes('instagram') || tokensContain(omMmcTokens, ['ig'])) {
        return MOTOM_TRAFFIC_SOURCE_CODES.INSTAGRAM;
    }

    if (omMmcLower.includes('tiktok') || omMmcLower.includes('bytedance') || tokensContain(omMmcTokens, ['tt'])) {
        return MOTOM_TRAFFIC_SOURCE_CODES.TIKTOK;
    }

    if (omMmcLower.includes('youtube') || tokensContain(omMmcTokens, ['yt'])) {
        return MOTOM_TRAFFIC_SOURCE_CODES.YOUTUBE;
    }

    return getTrafficSourceFromMotomSignals({ referrer, userAgent });
}

function getTrafficSourceFromOmMmc(omMmc, referrer, userAgent) {
    const omMmcTokens = getNormalizedTokens(omMmc);
    const source = omMmcTokens[0];

    if (!source) {
        return MOTOM_TRAFFIC_SOURCE_CODES.OTHER;
    }

    if (UNSUPPORTED_OM_MMC_SOURCES.has(source)) {
        return MOTOM_TRAFFIC_SOURCE_CODES.OTHER;
    }

    if (source === 'social') {
        return getDetailedSocialSource(omMmc, referrer, userAgent);
    }

    return OM_MMC_SOURCE_MAP[source] || MOTOM_TRAFFIC_SOURCE_CODES.OTHER;
}

function getTrafficSource() {
    const referrer = document.referrer || '';
    const userAgent = navigator.userAgent || '';

    const urlParams = new URLSearchParams(window.location.search);
    const omMmc = urlParams.get('om_mmc');

    if (omMmc) {
        return normalizeTrafficSourceCode(getTrafficSourceFromOmMmc(omMmc, referrer, userAgent));
    }

    return normalizeTrafficSourceCode(getTrafficSourceFromMotomSignals({ referrer, userAgent }));
}

function getDeviceOS() {
    const userAgent = navigator.userAgent || '';

    if (userAgent.includes('iPhone')) {
        return 'i';
    }

    if (userAgent.includes('Mac OS')) {
        return 'm';
    }

    if (userAgent.includes('Windows')) {
        return 'w';
    }

    if (userAgent.includes('Android')) {
        return 'a';
    }

    return 'm';
}

/** TTL for the standalone CSF dynamic commissioning data record (90 days, expressed in milliseconds — Storage.DAYS is one day in ms). */
const CSF_DYNAMIC_COMMISSIONING_DATA_STORAGE_TTL_MS = Storage.DAYS * 90;

/**
 * Persist the dynamic-commissioning `u5` (creator tier) value alongside the
 * `subAffiliateId` it was captured for. Stored in a dedicated localStorage
 * key (`CSF_DYNAMIC_COMMISSIONING_DATA`) so the new dynamic-commissioning
 * flow does not share state with the legacy `csfAttributionData` blob. The
 * record is shaped as a container envelope so future DC-only fields (e.g.
 * `u1`, `siteId`) can be added without splitting them across separate
 * localStorage keys; new fields will land via parallel setters that
 * read-modify-write the same record.
 *
 * Read-modify-writes the record so `activeCommissioning` (when present for the
 * same `subAffiliateId`) is preserved. When `subAffiliateId` changes, any prior
 * `activeCommissioning` is dropped — the new creator's active U1 is set by
 * `logCsfEntryInComponent` / `initializeActiveCommissioningU1State`.
 *
 * Cross-creator leakage protection layers:
 *  - **Write side**: this function is called from
 *    `captureCsfDynamicCommissioningU5FromProfile` on every creator change,
 *    so the record is updated with the new creator's `u5`/`subAffiliateId`
 *    whenever a fresh Profile API response arrives. On Profile API failure
 *    the caller invokes `clearCsfDynamicCommissioningU5`. When the feature
 *    flag flips off, the next capture clears the record.
 *  - **Read side**: {@link getCsfDynamicCommissioningU5} accepts an
 *    `expectedSubAffiliateId` and returns `null` on mismatch. Used by
 *    `initializeDcLinkSynergyAffiliate` to defend against the
 *    deferred-execution race on rapid SPA creator switches (A→B→C where C's
 *    capture overwrites storage before B's deferred ping macrotask fires).
 *
 * @param {{ u5: string|null, subAffiliateId: string|null }} params
 */
export function setCsfDynamicCommissioningU5({ u5 = null, subAffiliateId = null } = {}) {
    // SSR guard — `Storage.local` in node renders is built from
    // `errorMethods(...)` (functions that throw), so the typeof check
    // alone is not enough: node-render paths can have `global.window`
    // defined while `Storage.local` is still the throw-shim.
    if (typeof window === 'undefined' || Sephora.isNodeRender || !Storage?.local) {
        return;
    }

    try {
        // A record without a subAffiliateId is treated as malformed and refused —
        // without it the read-side mismatch guard cannot validate the record
        // against the current creator, defeating the cross-creator leakage
        // protection. Drop any prior record in this case so we don't silently
        // keep a stale tier around.
        if (!subAffiliateId) {
            Storage.local.removeItem(LOCAL_STORAGE.CSF_DYNAMIC_COMMISSIONING_DATA);

            return;
        }

        const existing = Storage.local.getItem(LOCAL_STORAGE.CSF_DYNAMIC_COMMISSIONING_DATA) || {};
        const sameCreator = !existing.subAffiliateId || existing.subAffiliateId === subAffiliateId;
        const { activeCommissioning: existingActive, ...rest } = existing;

        Storage.local.setItem(
            LOCAL_STORAGE.CSF_DYNAMIC_COMMISSIONING_DATA,
            {
                ...rest,
                u5: u5 ?? null,
                subAffiliateId,
                updatedAt: Date.now(),
                ...(sameCreator && existingActive ? { activeCommissioning: existingActive } : {})
            },
            CSF_DYNAMIC_COMMISSIONING_DATA_STORAGE_TTL_MS
        );
    } catch (err) {
        Sephora.logger.error('CSF dynamic commissioning: failed to persist u5', err);
    }
}

/**
 * Reads the persisted dynamic-commissioning `u5`. Returns `null` when no
 * record exists, the record is malformed, storage is unavailable, or — when
 * `expectedSubAffiliateId` is supplied — the persisted `subAffiliateId` does
 * not match the expected one.
 *
 * The optional mismatch guard exists so consumers that build a payload from
 * a single creator-context snapshot (e.g. `initializeDcLinkSynergyAffiliate`
 * fired via `deferTaskExecution`) can avoid attaching another creator's u5
 * if a later creator switch overwrote storage between the schedule and the
 * deferred execution. When `expectedSubAffiliateId` is omitted, behavior is
 * the legacy `return record.u5 if present` — used by `logCsfPageView` where
 * the call is sync with the navigation handler and no race window exists.
 *
 * @param {{ expectedSubAffiliateId?: string|null }} [options]
 * @returns {string|null}
 */
export function getCsfDynamicCommissioningU5({ expectedSubAffiliateId = null } = {}) {
    // SSR guard — see `setCsfDynamicCommissioningU5` for the rationale.
    if (typeof window === 'undefined' || Sephora.isNodeRender || !Storage?.local) {
        return null;
    }

    try {
        const record = Storage.local.getItem(LOCAL_STORAGE.CSF_DYNAMIC_COMMISSIONING_DATA);

        if (!record || typeof record.u5 !== 'string' || record.u5.length === 0) {
            return null;
        }

        // Mismatch guard: when caller supplied an expected subAffiliateId and
        // the persisted record was captured for a different creator, omit u5
        // so the caller's payload is built strictly from one creator context.
        if (expectedSubAffiliateId && record.subAffiliateId !== expectedSubAffiliateId) {
            return null;
        }

        return record.u5;
    } catch {
        return null;
    }
}

/**
 * Clears the persisted dynamic-commissioning data record (currently holding
 * `u5` + `subAffiliateId`; future DC-only fields like `u1`/`siteId` will share the
 * same record and be cleared together).
 */
export function clearCsfDynamicCommissioningU5() {
    // SSR guard — see `setCsfDynamicCommissioningU5` for the rationale.
    if (typeof window === 'undefined' || Sephora.isNodeRender || !Storage?.local) {
        return;
    }

    try {
        Storage.local.removeItem(LOCAL_STORAGE.CSF_DYNAMIC_COMMISSIONING_DATA);
    } catch {
        // best-effort; nothing else to do.
    }
}

/**
 * Persists the Dynamic Commissioning active/current U1 state for the active
 * CSF journey alongside the existing `u5` fields under the same
 * `CSF_DYNAMIC_COMMISSIONING_DATA` record.
 *
 * CSF-1791: `linkshareSiteId` (Creator Profile API input) and `siteId`
 * (returned from `/rakuten/site-id`, used as the event `siteId`) are kept
 * as separate persisted fields. They must never be conflated — the input
 * comes from the profile, the output comes from the Rakuten ping.
 *
 * @param {{
 *   u1: string,
 *   linkshareSiteId: string | null,
 *   siteId: string | null,
 *   mid: string | number | null,
 *   merchantTag: string | null,
 *   storefrontTag: string | null,
 *   hostType: string,
 *   trafficSource: string,
 *   device: string,
 *   subAffiliateId: string
 * }} state
 */
export function setActiveCommissioningU1State(state) {
    if (typeof window === 'undefined' || Sephora.isNodeRender || !Storage?.local) {
        return;
    }

    try {
        if (!state || !state.subAffiliateId || !state.u1) {
            return;
        }

        const existing = Storage.local.getItem(LOCAL_STORAGE.CSF_DYNAMIC_COMMISSIONING_DATA) || {};
        const sameCreator = !existing.subAffiliateId || existing.subAffiliateId === state.subAffiliateId;
        const preservedState = sameCreator ? existing : {};

        Storage.local.setItem(
            LOCAL_STORAGE.CSF_DYNAMIC_COMMISSIONING_DATA,
            {
                ...preservedState,
                subAffiliateId: state.subAffiliateId,
                updatedAt: Date.now(),
                activeCommissioning: {
                    u1: state.u1,
                    linkshareSiteId: state.linkshareSiteId ?? null,
                    siteId: state.siteId ?? null,
                    mid: state.mid ?? null,
                    merchantTag: state.merchantTag ?? null,
                    storefrontTag: state.storefrontTag ?? null,
                    hostType: state.hostType,
                    trafficSource: state.trafficSource,
                    device: state.device,
                    subAffiliateId: state.subAffiliateId
                }
            },
            CSF_DYNAMIC_COMMISSIONING_DATA_STORAGE_TTL_MS
        );
    } catch (err) {
        Sephora.logger.error('CSF dynamic commissioning: failed to persist active U1 state', err);
    }
}

/**
 * Reads the persisted Dynamic Commissioning active/current U1 state.
 *
 * @param {{ expectedSubAffiliateId?: string|null }} [options]
 * @returns {object | null}
 */
export function getActiveCommissioningU1State({ expectedSubAffiliateId = null } = {}) {
    if (typeof window === 'undefined' || Sephora.isNodeRender || !Storage?.local) {
        return null;
    }

    try {
        const record = Storage.local.getItem(LOCAL_STORAGE.CSF_DYNAMIC_COMMISSIONING_DATA);
        const active = record?.activeCommissioning;

        if (!active || typeof active.u1 !== 'string' || active.u1.length === 0) {
            return null;
        }

        // CSF-1791 migration: CSF-1773 persisted the field as `siteId` and
        // stored the `linkshareSiteId` VALUE under it. CSF-1791 splits them
        // into two distinct keys (`linkshareSiteId` = Profile API input,
        // `siteId` = Rakuten response). Legacy records lack `linkshareSiteId`
        // entirely, which would silently block `applyProductInteractionToActiveCommissioningU1`
        // (its fetch input resolves to undefined → null return → no U1 update).
        // Return null so `initializeActiveCommissioningU1State` rebuilds from
        // the creator profile and overwrites in the new shape. We do not
        // synthesize `linkshareSiteId ?? siteId` at call sites — in the new
        // shape `siteId` is the Rakuten response (NOT linkshareSiteId), so
        // such a fallback would corrupt the ping input when `linkshareSiteId`
        // is explicitly `null`.
        if (!('linkshareSiteId' in active)) {
            return null;
        }

        if (expectedSubAffiliateId && active.subAffiliateId !== expectedSubAffiliateId) {
            return null;
        }

        return active;
    } catch {
        return null;
    }
}

/**
 * Clears only the `activeCommissioning` sub-key of `CSF_DYNAMIC_COMMISSIONING_DATA`.
 */
export function clearActiveCommissioningU1State() {
    if (typeof window === 'undefined' || Sephora.isNodeRender || !Storage?.local) {
        return;
    }

    try {
        const record = Storage.local.getItem(LOCAL_STORAGE.CSF_DYNAMIC_COMMISSIONING_DATA);

        if (!record || !record.activeCommissioning) {
            return;
        }

        const { activeCommissioning: _drop, ...rest } = record;

        Storage.local.setItem(
            LOCAL_STORAGE.CSF_DYNAMIC_COMMISSIONING_DATA,
            { ...rest, updatedAt: Date.now() },
            CSF_DYNAMIC_COMMISSIONING_DATA_STORAGE_TTL_MS
        );
    } catch {
        // best-effort
    }
}

/**
 * Feature-flag check for CSF dynamic commissioning.
 *
 * @returns {boolean}
 */
export function isDynamicCommissioningEnabled() {
    return Sephora.configurationSettings?.csf?.dynamicCommissioning?.isEnabled === true;
}

/**
 * Captures the dynamic-commissioning `u5` directly from a freshly-loaded
 * creator Profile API response and persists it under `CSF_DYNAMIC_COMMISSIONING_DATA`. This is the
 * single dynamic-commissioning capture point — it is fully self-contained and
 * does not read from or mutate the legacy `csfAttributionData` blob.
 *
 * Behavior:
 * - Feature flag off → clears any prior record and returns.
 * - Profile has a non-empty `u5` → persists it with the profile's `subAffiliateId`.
 * - Profile has no `u5` (or empty/whitespace) → persists `null` under the
 *   current `subAffiliateId` (graceful fallback per CSF-1725 AC).
 * - Missing `subAffiliateId` → strict guard in `setCsfDynamicCommissioningU5`
 *   removes the prior record (cannot validate on read otherwise).
 *
 * @param {object} [creatorProfile] Fresh response from the creator Profile API.
 */
export function captureCsfDynamicCommissioningU5FromProfile(creatorProfile) {
    if (!isDynamicCommissioningEnabled()) {
        clearCsfDynamicCommissioningU5();

        return;
    }

    const attributionDetails = creatorProfile?.attributionDetails || Empty.Object;
    const { subAffiliateId, u5 } = attributionDetails;
    const trimmedU5 = typeof u5 === 'string' ? u5.trim() : null;
    const u5Value = trimmedU5 && trimmedU5.length > 0 ? trimmedU5 : null;

    setCsfDynamicCommissioningU5({ u5: u5Value, subAffiliateId: subAffiliateId ?? null });
}

export const CSF_JOURNEYS_KEY = 'csf_journeys';

/** Journey TTL — 15 days from entry timestamp (dynamic commissioning schema). */
export const CSF_JOURNEY_TTL_MS = 15 * 24 * 60 * 60 * 1000;

/**
 * Embedded fallback for CSF Dynamic Commissioning rules (RQL AST v2). Used when
 * `Sephora.configurationSettings.csf.dynamicCommissioning.rules` is missing or
 * cannot be parsed as JSON. Source: CSF-1739 attachment `rql-rules-fallback-v2.json`.
 */

const DEFAULT_DC_RULES = {
    version: '2.2.0',
    rules: [
        {
            ['rule_id']: 'AFFILIATE_EXTERNAL_BLOCKED',
            query: [
                'REQUIRE',
                ['NOT', ['CONTAINS', 'events', 'om_mmc', 'motom']],
                ['FALLBACK', ['WHEN_ENTRY_FROM', 'events', 'rakuten\\.com|linksynergy\\.com'], ['CONTAINS', 'events', 'om_mmc', '^aff$']]
            ],
            target: null
        },
        {
            ['rule_id']: 'SOCIAL_CREATOR_POSTED_FIRST_TOUCH',
            query: [
                'MIN',
                [
                    'WITH_CREATOR',
                    [
                        'REQUIRE',
                        ['WHEN_ENTRY_FROM', 'events', 'instagram\\.com|tiktok\\.com|facebook\\.com'],
                        ['NOT', ['CONTAINS', 'events', 'om_mmc', '.+']]
                    ]
                ]
            ],
            target: 'creator_id'
        },
        {
            ['rule_id']: 'DEFAULT_FALLBACK_NO_MATCH',
            query: ['REQUIRE', ['ENTRY', ['ALL', 'events']], ['FIRST', ['WITH_CREATOR', ['ALL', 'events']]]],
            target: 'creator_id'
        }
    ]
};

/**
 * Returns CSF Dynamic Commissioning rules from ConfigHub
 * (`Sephora.configurationSettings.csf.dynamicCommissioning.rules`, a JSON string),
 * falling back to the embedded {@link DEFAULT_DC_RULES} when the value is
 * missing or fails to parse.
 *
 * @returns {{ version: string, rules: Array<object> }}
 */
export function getDynamicCommissioningRules() {
    const raw = Sephora?.configurationSettings?.csf?.dynamicCommissioning?.rules;

    if (raw == null || (typeof raw === 'string' && raw.trim() === '')) {
        Sephora.logger.verbose('CSF dynamic commissioning: ConfigHub rules missing or empty — using embedded defaults');

        return JSON.parse(JSON.stringify(DEFAULT_DC_RULES));
    }

    try {
        const parsed = JSON.parse(raw);

        if (parsed && typeof parsed === 'object' && typeof parsed.version === 'string' && Array.isArray(parsed.rules) && parsed.rules.length > 0) {
            return parsed;
        }

        Sephora.logger.verbose('CSF dynamic commissioning: ConfigHub rules malformed or empty — using embedded defaults', {
            hasVersion: typeof parsed?.version === 'string',
            rulesIsArray: Array.isArray(parsed?.rules),
            rulesLength: Array.isArray(parsed?.rules) ? parsed.rules.length : null
        });
    } catch (err) {
        Sephora.logger.verbose('CSF dynamic commissioning: ConfigHub rules JSON parse failed — using embedded defaults', err);
    }

    return JSON.parse(JSON.stringify(DEFAULT_DC_RULES));
}

/**
 * Parses a LinkSynergy / Rakuten timestamp string to epoch ms.
 *
 * @param {string} linkshareTimeStamp
 * @returns {number}
 */
function parseAffiliateTimestampMs(linkshareTimeStamp) {
    const isoTime = dateUtils.restoreISO8601Format(linkshareTimeStamp);

    return isoTime ? new Date(isoTime).getTime() : NaN;
}

/**
 * Read-only: external (Rakuten / LinkSynergy) affiliate timestamp for checkout precedence.
 * Mirrors `analyticsUtils.getAffiliateGatewayParams()` sources. Used only to decide whether
 * DC/RQL may run — not to build the DC submit-order payload.
 *
 * @returns {{ siteId: string, linkshareTimeStamp: string, timestampMs: number }|null}
 */
export function getExternalAffiliateAttribution() {
    if (typeof window === 'undefined' || Sephora.isNodeRender || !Storage?.local) {
        return null;
    }

    let affiliateGatewayParams;

    try {
        affiliateGatewayParams = Storage.local.getItem(anaConsts.AFFILIATE_PARAMETERS.AFFILIATE_PARAMETERS_STORAGE);
    } catch (err) {
        Sephora.logger.verbose('CSF dynamic commissioning: failed to read affiliate gateway storage for precedence', err);
        affiliateGatewayParams = null;
    }

    if (affiliateGatewayParams) {
        if (!affiliateGatewayParams.siteId || !affiliateGatewayParams.linkshareTimeStamp) {
            return null;
        }

        const timestampMs = parseAffiliateTimestampMs(affiliateGatewayParams.linkshareTimeStamp);

        if (!Number.isFinite(timestampMs)) {
            return null;
        }

        return {
            siteId: affiliateGatewayParams.siteId,
            linkshareTimeStamp: affiliateGatewayParams.linkshareTimeStamp,
            timestampMs
        };
    }

    try {
        const cookieTimeStamp = cookieUtil.read(anaConsts.AFFILIATE_PARAMETERS.AFFILIATE_LINKSHARETIME_COOKIE) || '';
        const cookieSiteId = cookieUtil.read(anaConsts.AFFILIATE_PARAMETERS.AFFILIATE_SITEID_COOKIE) || '';

        if (!cookieSiteId || cookieTimeStamp === '') {
            return null;
        }

        const linkshareTimeFromCookie = cookieTimeStamp !== '' ? dateUtils.restoreISO8601Format(cookieTimeStamp).split('|')[0] : '';
        const timestampMs = linkshareTimeFromCookie ? new Date(linkshareTimeFromCookie).getTime() : NaN;

        if (!Number.isFinite(timestampMs)) {
            return null;
        }

        return {
            siteId: cookieSiteId,
            linkshareTimeStamp: cookieTimeStamp,
            timestampMs
        };
    } catch (err) {
        Sephora.logger.verbose('CSF dynamic commissioning: failed to read affiliate cookies for precedence', err);

        return null;
    }
}

/**
 * Whether checkout should run RQL for DC (CSF journey must be more recent than external affiliate).
 *
 * @param {{ csfTimestampMs: number|null, externalAffiliate: ReturnType<typeof getExternalAffiliateAttribution> }} params
 * @returns {boolean}
 */
export function shouldRunCheckoutDynamicCommissioning({ csfTimestampMs, externalAffiliate }) {
    if (!Number.isFinite(csfTimestampMs)) {
        return false;
    }

    if (!externalAffiliate) {
        return true;
    }

    return csfTimestampMs > externalAffiliate.timestampMs;
}

/**
 * Loads all events from non-expired CSF journeys that contain an entry event,
 * plus the latest CSF event timestamp used for read-only affiliate precedence.
 *
 * @returns {Promise<{ events: Array<object>, csfTimestampMs: number|null, hasCsfJourney: boolean }>}
 */
export async function getCsfIndexedDbEventsForCheckout() {
    const emptyResult = {
        events: [],
        csfTimestampMs: null,
        hasCsfJourney: false
    };

    if (!isDynamicCommissioningEnabled() || Sephora.isNodeRender) {
        return emptyResult;
    }

    if (typeof Storage.db?.getItem !== 'function') {
        return emptyResult;
    }

    try {
        const data = await Storage.db.getItem(CSF_JOURNEYS_KEY, true, false, false, Tables.CsfAttribution);
        const journeys = Array.isArray(data?.journeys) ? data.journeys : [];
        const now = Date.now();
        const events = [];
        let csfTimestampMs = null;

        /* eslint-disable camelcase */
        const activeCsfJourneys = journeys.filter(journey => {
            if (!journey) {
                return false;
            }

            const { expires_at } = journey;

            if (typeof expires_at !== 'number' || expires_at <= now) {
                return false;
            }

            const journeyEvents = Array.isArray(journey.events) ? journey.events : [];

            return journeyEvents.some(event => event?.event_type === CSF_EVENT_TYPES.ENTRY);
        });
        /* eslint-enable camelcase */

        for (const journey of activeCsfJourneys) {
            const journeyEvents = Array.isArray(journey.events) ? journey.events : [];

            for (const event of journeyEvents) {
                events.push(event);

                if (typeof event?.timestamp === 'number') {
                    csfTimestampMs = csfTimestampMs == null ? event.timestamp : Math.max(csfTimestampMs, event.timestamp);
                }
            }
        }

        events.sort((a, b) => (a?.timestamp || 0) - (b?.timestamp || 0));

        return {
            events,
            csfTimestampMs,
            hasCsfJourney: activeCsfJourneys.length > 0
        };
    } catch (err) {
        Sephora.logger.error('CSF dynamic commissioning: failed to read IndexedDB events for checkout', err);

        return emptyResult;
    }
}

/**
 * Formats an epoch-ms timestamp into the submit-order `linkshareTime` string.
 *
 * @param {number} timestampMs
 * @returns {string}
 */
function formatEventTimestampMsToLinkshareTime(timestampMs) {
    if (!Number.isFinite(timestampMs)) {
        return '';
    }

    try {
        return dateUtils.restoreISO8601Format(dateUtils.formatISODateVariation(timestampMs));
    } catch {
        return '';
    }
}

/**
 * Formats an RQL `timestamp` field (number or parseable date string) for submit-order.
 *
 * @param {number|string|null|undefined} timestamp
 * @returns {string}
 */
function formatRqlTimestampToLinkshareTime(timestamp) {
    if (timestamp == null) {
        return '';
    }

    const timestampMs = typeof timestamp === 'number' ? timestamp : new Date(timestamp).getTime();

    return formatEventTimestampMsToLinkshareTime(timestampMs);
}

/**
 * Derives submit-order `linkshareTime` from the RQL attribution outcome and journey events.
 * Prefers the RQL timestamp; when missing or invalid, uses the latest matching CSF event
 * (by `siteId`, then `u1`) so the DC path does not fall back to legacy affiliate storage.
 *
 * @param {{ siteId?: string, u1?: string|null, timestamp?: number|string|null }} rqlResult
 * @param {Array<object>} events
 * @returns {string}
 */
function deriveLinkshareTimeFromRqlAttribution(rqlResult, events) {
    const fromRqlTimestamp = formatRqlTimestampToLinkshareTime(rqlResult?.timestamp);

    if (fromRqlTimestamp) {
        return fromRqlTimestamp;
    }

    if (!Array.isArray(events) || !rqlResult?.siteId) {
        return '';
    }

    const findLatestEventTimestampMs = predicate => {
        let latestTimestampMs = null;

        for (const event of events) {
            if (predicate(event) && typeof event?.timestamp === 'number') {
                latestTimestampMs = latestTimestampMs == null ? event.timestamp : Math.max(latestTimestampMs, event.timestamp);
            }
        }

        return latestTimestampMs;
    };

    const siteIdMatchTimestampMs = findLatestEventTimestampMs(event => event?.siteId === rqlResult.siteId);

    if (siteIdMatchTimestampMs != null) {
        return formatEventTimestampMsToLinkshareTime(siteIdMatchTimestampMs);
    }

    if (rqlResult.u1) {
        const u1MatchTimestampMs = findLatestEventTimestampMs(event => event?.u1 === rqlResult.u1);

        if (u1MatchTimestampMs != null) {
            return formatEventTimestampMsToLinkshareTime(u1MatchTimestampMs);
        }
    }

    return '';
}

/**
 * DC submit-order attribution is complete only when RQL supplied siteId, ruleId, and linkshareTime.
 * Incomplete results must not produce a partial `linkshareInformation` payload.
 *
 * @param {{ siteId?: string, linkshareTime?: string, rule_id?: string|null }} checkoutCommissioning
 * @returns {boolean}
 */
function isCompleteDcCheckoutCommissioning(checkoutCommissioning) {
    return Boolean(checkoutCommissioning?.siteId && checkoutCommissioning?.linkshareTime && checkoutCommissioning?.rule_id);
}

/**
 * At checkout: when DC is enabled and a CSF journey exists, compares the latest CSF event
 * timestamp to legacy affiliate storage/cookies (read-only). Runs RQL only when CSF is
 * more recent; the RQL result then drives the DC submit-order payload (`siteId`, `ruleId`,
 * `linkshareTime`). Does not write legacy storage or perform a new Rakuten ping here.
 * BOPIS checkout may persist the DC triple to `affiliateGatewayParameters` after
 * submitOrder succeeds via {@link syncBopisDcAttributionAfterPlaceOrderSuccess} (shipping does not).
 *
 * When DC is disabled, CSF loses recency, RQL yields an incomplete attribution triple
 * (`siteId`, `linkshareTime`, `rule_id`), or evaluation fails, returns `null` so callers
 * fall back to legacy `affiliateGatewayParameters` via {@link buildPlaceOrderLinkshareInformation}.
 *
 * @returns {Promise<{ rule_id: string, siteId: string, linkshareTime: string }|null>}
 *   Non-null results always include a complete RQL triple; incomplete outcomes return `null`.
 */
export async function resolveCheckoutDynamicCommissioning() {
    if (!isDynamicCommissioningEnabled()) {
        return null;
    }

    const { events, csfTimestampMs, hasCsfJourney } = await getCsfIndexedDbEventsForCheckout();

    if (!hasCsfJourney) {
        return null;
    }

    const externalAffiliate = getExternalAffiliateAttribution();

    if (!shouldRunCheckoutDynamicCommissioning({ csfTimestampMs, externalAffiliate })) {
        const skipReason = Number.isFinite(csfTimestampMs)
            ? 'CSF dynamic commissioning: skipping checkout RQL — external affiliate is more recent'
            : 'CSF dynamic commissioning: skipping checkout RQL — CSF timestamp is missing or invalid';

        Sephora.logger.verbose(skipReason, {
            csfTimestampMs,
            externalTimestampMs: externalAffiliate?.timestampMs
        });

        return null;
    }

    const { rules } = getDynamicCommissioningRules();
    let rqlResult;

    try {
        const { evaluate } = await import('components/CreatorStoreFront/helpers/rqlEvaluator');
        rqlResult = await evaluate(rules, events);
    } catch (err) {
        Sephora.logger.error('CSF dynamic commissioning: checkout RQL evaluation failed', err);

        return null;
    }

    if (!rqlResult?.siteId) {
        Sephora.logger.verbose('CSF dynamic commissioning: checkout RQL produced no siteId', rqlResult);

        return null;
    }

    const linkshareTime = deriveLinkshareTimeFromRqlAttribution(rqlResult, events);

    /* eslint-disable camelcase */
    const checkoutCommissioning = {
        ['rule_id']: rqlResult.rule_id ?? null,
        siteId: rqlResult.siteId,
        linkshareTime
    };
    /* eslint-enable camelcase */

    if (!isCompleteDcCheckoutCommissioning(checkoutCommissioning)) {
        Sephora.logger.verbose('CSF dynamic commissioning: incomplete checkout RQL outcome — skipping DC payload', checkoutCommissioning);

        return null;
    }

    return checkoutCommissioning;
}

/**
 * Builds `linkshareInformation` for `checkoutApi.placeOrder` from CSF checkout resolution
 * and/or legacy affiliate gateway params. Prefer {@link prepareCheckoutPlaceOrderLinkshareInformation}
 * at call sites so journey cleanup and commissioning resolution stay in one place.
 *
 * @param {{ rule_id?: string|null, siteId?: string, linkshareTime?: string }|null} checkoutCommissioning
 * @param {{ linkshareSiteId?: string, linkshareTime?: string }} [affiliateGatewayParams]
 * @returns {{ linkshareSiteId: string, linkshareTime: string, ruleId: string }|{ linkshareSiteId: string, linkshareTime: string }|null}
 *   Non-null returns always include `linkshareTime` (required by submitOrder). The DC path
 *   (complete RQL triple) also includes `ruleId`; the legacy fallback path omits `ruleId`.
 */
export function buildPlaceOrderLinkshareInformation(checkoutCommissioning, affiliateGatewayParams = {}) {
    if (isCompleteDcCheckoutCommissioning(checkoutCommissioning)) {
        return {
            linkshareSiteId: checkoutCommissioning.siteId,
            linkshareTime: checkoutCommissioning.linkshareTime,
            ruleId: checkoutCommissioning.rule_id
        };
    }

    const { linkshareSiteId = '', linkshareTime = '' } = affiliateGatewayParams;

    if (linkshareSiteId !== '' && linkshareTime !== '') {
        return {
            linkshareSiteId,
            linkshareTime
        };
    }

    return null;
}

/**
 * BOPIS checkout: writes DC RQL `siteId` and persists `linkshareTime` as `linkshareTimeStamp`
 * in `affiliateGatewayParameters` so legacy Rakuten readers use updated attribution without a new ping.
 * Mirrors the storage shape updated by legacy `syncAffiliateGatewaySiteIdFromApi` in
 * `csfAttribution.js` (siteId + `linkshareTimeStamp` only; other fields preserved).
 *
 * @param {{ siteId: string, linkshareTime: string }} params
 * @returns {boolean} `true` when affiliate gateway storage was updated; `false` when inputs are invalid or `linkshareTime` is not parseable (avoids new siteId + stale timestamp)
 */
export function syncDcCheckoutAttributionToAffiliateGatewayStorage({ siteId, linkshareTime }) {
    if (!siteId || !linkshareTime || typeof window === 'undefined' || Sephora.isNodeRender || !Storage?.local) {
        return false;
    }

    const timestampMs = new Date(linkshareTime).getTime();

    if (!Number.isFinite(timestampMs)) {
        Sephora.logger.verbose('CSF dynamic commissioning: skipping affiliate gateway sync — linkshareTime is not parseable', {
            siteId,
            linkshareTime
        });

        return false;
    }

    try {
        const DEFAULT_COOKIE_LIFETIME = anaConsts.AFFILIATE_PARAMETERS.AFFILIATE_COOKIE_LIFETIME;
        const currentData = Storage.local.getItem(anaConsts.AFFILIATE_PARAMETERS.AFFILIATE_PARAMETERS_STORAGE) || {};
        const newData = JSON.parse(JSON.stringify(currentData));

        if (!newData.params) {
            newData.params = {};
        }

        if (!Array.isArray(newData.params.siteID)) {
            newData.params.siteID = [];
        }

        newData.params.siteID[0] = siteId;
        newData.siteId = siteId;
        newData.linkshareTimeStamp = dateUtils.formatISODateVariation(timestampMs);

        Storage.local.setItem(anaConsts.AFFILIATE_PARAMETERS.AFFILIATE_PARAMETERS_STORAGE, newData, DEFAULT_COOKIE_LIFETIME * Storage.DAYS);
        Sephora.logger.verbose('CSF dynamic commissioning: synced checkout DC attribution to affiliate gateway storage', {
            siteId
        });

        return true;
    } catch (err) {
        Sephora.logger.error('CSF dynamic commissioning: failed to sync checkout DC attribution to affiliate gateway storage', err);

        return false;
    }
}

/**
 * BOPIS only: after `submitOrder` succeeds, sync DC `linkshareInformation` into
 * `affiliateGatewayParameters`. Avoids writing legacy storage before place order so a
 * failed submit + retry does not make DC compete against its own prior write at precedence.
 *
 * @param {{ isBopis?: boolean, linkshareInformation?: { linkshareSiteId?: string, linkshareTime?: string, ruleId?: string }|null }} params
 * @returns {boolean} `true` when affiliate gateway storage was updated
 */
export function syncBopisDcAttributionAfterPlaceOrderSuccess({ isBopis = false, linkshareInformation } = {}) {
    if (!isBopis || !linkshareInformation?.ruleId) {
        return false;
    }

    const { linkshareSiteId, linkshareTime } = linkshareInformation;

    if (!linkshareSiteId || !linkshareTime) {
        return false;
    }

    return syncDcCheckoutAttributionToAffiliateGatewayStorage({
        siteId: linkshareSiteId,
        linkshareTime
    });
}

/** Canonical `event_type` strings for CSF dynamic commissioning journeys. */
export const CSF_EVENT_TYPES = {
    ENTRY: 'entry',
    INTERNAL_SEARCH: 'internal_search',
    PAGE_VIEW: 'page_view',
    ADD_TO_CART: 'add_to_cart',
    QUICKLOOK_ATB: 'quicklook_atb',
    EXIT: 'exit'
};

/**
 * Builds `u1` / site / mid / creator ids for dynamic commissioning from the creator profile and route,
 * using this module's local Dynamic Commissioning string-building helpers.
 * These helpers were derived from legacy CSF attribution logic but are intentionally
 * isolated in this module and may diverge from legacy fresh-write behavior in some cases.
 * Reads only the fresh Profile API response — does NOT read `CSF_ATTRIBUTION_DATA`,
 * so the returned `u1` may differ both from the reused-within-5-days U1 stored there
 * and from a legacy `captureAttributionData` fresh write when DC behavior intentionally diverges.
 *
 * CSF-1791: `linkshareSiteId` is returned as the Rakuten ping input — NOT as
 * the event `siteId`. The event `siteId` is the value returned from
 * `/rakuten/site-id` and is captured later by the LinkSynergy fetch path.
 *
 * @param {{ creatorProfile?: object, section?: string, identifier?: string|null }} params
 * @returns {{ u1: string, linkshareSiteId: string|null, mid: string|number|null, subAffiliateId: string }|null}
 */
export function buildCommissioningAttributionFieldsFromProfile({ creatorProfile, section, identifier = null } = {}) {
    if (!isDynamicCommissioningEnabled()) {
        return null;
    }

    if (!creatorProfile) {
        return null;
    }

    const attributionDetails = creatorProfile?.attributionDetails || Empty.Object;
    const {
        linkshareSiteId, merchantTag, mid, storefrontTag, subAffiliateId
    } = attributionDetails;

    if (!subAffiliateId) {
        return null;
    }

    const pageType = getPageTypeForAttribution(section, identifier);
    const pageIdentifierLocation = generatePageIdentifierLocation(subAffiliateId, section, identifier);

    /* eslint-disable camelcase */
    const u1 = constructU1Param({
        host_type: 'b',
        traffic_source: getTrafficSource(),
        device: getDeviceOS(),
        page_type: pageType,
        identifier_location: pageIdentifierLocation,
        merchant_tag: merchantTag,
        storefront_tag: storefrontTag
    });
    /* eslint-enable camelcase */

    return {
        u1,
        linkshareSiteId: linkshareSiteId || null,
        mid: mid ?? null,
        subAffiliateId
    };
}

function isMissingRequiredValue(value) {
    return value === null || value === undefined || value === '';
}

/**
 * Dynamic Commissioning thin wrapper around the shared low-level
 * `getSiteIdFromLinkSynergy` HTTP service. Parallel to the legacy
 * `fetchSiteIdFromLinkSynergy` in `csfAttribution.js` — duplicated
 * intentionally to keep DC and legacy fully decoupled (the DC orchestrator
 * never reaches into legacy modules to make its adapter call).
 *
 * Accepts `u5` as a first-class parameter (unlike the legacy wrapper, which
 * never carries DC fields). The shared low-level service omits `u5` from
 * the payload when it is `undefined`/empty — graceful fallback.
 *
 * @param {string} linkshareSiteId
 * @param {string|number} mid
 * @param {string} murl
 * @param {string} u1
 * @param {string} [u5]
 * @returns {Promise<{data: object, headers: object}>} Low-level API result object
 */
export async function fetchDcSiteIdFromLinkSynergy(linkshareSiteId, mid, murl, u1, u5) {
    try {
        const result = await getSiteIdFromLinkSynergy(linkshareSiteId, mid, murl, u1, u5);

        Sephora.logger.verbose('CSF dynamic commissioning: LinkSynergy site ID fetched', result.data);

        return result;
    } catch (error) {
        Sephora.logger.error('CSF dynamic commissioning: error fetching site ID from LinkSynergy', error);
        throw error;
    }
}

/**
 * CSF-1791 internal helper. Pings `/rakuten/site-id` with the given U1 input
 * and returns the response `siteID` (or `null` on any failure / missing
 * inputs / SSR). Does NOT touch persistence — the caller decides when and
 * how to persist `{ u1, siteId }` together.
 *
 * @param {{ linkshareSiteId: string|null, mid: string|number|null, u1: string|null, expectedSubAffiliateId?: string|null }} params
 * @returns {Promise<string|null>}
 */
async function fetchDcEventSiteIdForU1({ linkshareSiteId, mid, u1, expectedSubAffiliateId = null }) {
    if (typeof window === 'undefined' || Sephora.isNodeRender) {
        return null;
    }

    if (!linkshareSiteId || isMissingRequiredValue(mid) || !u1) {
        return null;
    }

    const u5 = getCsfDynamicCommissioningU5({ expectedSubAffiliateId }) ?? undefined;

    try {
        const result = await fetchDcSiteIdFromLinkSynergy(linkshareSiteId, mid, window.location.href, u1, u5);
        const siteID = result?.data?.siteID;

        return typeof siteID === 'string' && siteID.length > 0 ? siteID : null;
    } catch (err) {
        // `fetchDcSiteIdFromLinkSynergy` rethrows; swallow here so callers
        // gate on the return value (`null` ⇒ graceful skip per CSF-1791 AC:
        // "If siteId fetch fails, skip gracefully rather than logging a new
        // U1 with a stale/mismatched siteId").
        Sephora.logger.verbose('CSF dynamic commissioning: siteId fetch failed (handled)', err);

        return null;
    }
}

function buildActiveCommissioningU1StateFromProfile({ creatorProfile, section, identifier = null } = {}) {
    if (!isDynamicCommissioningEnabled() || !creatorProfile) {
        return null;
    }

    const attributionDetails = creatorProfile?.attributionDetails || Empty.Object;
    const {
        linkshareSiteId, merchantTag, mid, storefrontTag, subAffiliateId
    } = attributionDetails;

    if (!subAffiliateId) {
        return null;
    }

    const pageType = getPageTypeForAttribution(section, identifier);
    const pageIdentifierLocation = generatePageIdentifierLocation(subAffiliateId, section, identifier);
    const trafficSource = getTrafficSource();
    const device = getDeviceOS();
    const hostType = 'b';

    /* eslint-disable camelcase */
    const u1 = constructU1Param({
        host_type: hostType,
        traffic_source: trafficSource,
        device,
        page_type: pageType,
        identifier_location: pageIdentifierLocation,
        merchant_tag: merchantTag,
        storefront_tag: storefrontTag
    });
    /* eslint-enable camelcase */

    // CSF-1791: `linkshareSiteId` is the Rakuten ping input from the Creator
    // Profile API. `siteId` (event field) is intentionally left null here —
    // it is populated by the caller after a successful `/rakuten/site-id`
    // response so that `{ u1, siteId }` are persisted together as a pair.
    return {
        u1,
        linkshareSiteId: linkshareSiteId || null,
        siteId: null,
        mid: mid ?? null,
        merchantTag: merchantTag ?? null,
        storefrontTag: storefrontTag ?? null,
        hostType,
        trafficSource,
        device,
        subAffiliateId
    };
}

/**
 * Initializes active/current U1 for reuse by page_view / ATB / Rakuten without
 * logging an `entry` event (e.g. creator switch paths governed by CSF-1699).
 *
 * CSF-1791: fetches the matching `siteId` from `/rakuten/site-id` for the
 * built U1 and persists `{ u1, siteId }` together. On fetch failure, persists
 * the baseline state with `siteId: null` so downstream events that require
 * `siteId` (entry / ATB) skip gracefully rather than logging a U1 paired
 * with a stale siteId from a previous creator.
 *
 * CSF-1791 race defense (A→B→C rapid creator switch): the `/rakuten/site-id`
 * fetch is awaited before persistence, so a slow earlier call can resolve
 * after a newer call has already committed. Callers can pass an optional
 * `isStillCurrent` predicate; if it returns `false` immediately before the
 * persistence write, the write is skipped and `null` is returned so the
 * stale call cannot overwrite the newer creator's `{ u1, siteId }` pair.
 *
 * @param {{ creatorProfile?: object, section?: string, identifier?: string|null, isStillCurrent?: () => boolean }} params
 * @returns {Promise<object | null>}
 */
export async function initializeActiveCommissioningU1State({ creatorProfile, section, identifier = null, isStillCurrent = null } = {}) {
    const state = buildActiveCommissioningU1StateFromProfile({ creatorProfile, section, identifier });

    if (!state) {
        return null;
    }

    const siteId = await fetchDcEventSiteIdForU1({
        linkshareSiteId: state.linkshareSiteId,
        mid: state.mid,
        u1: state.u1,
        expectedSubAffiliateId: state.subAffiliateId
    });

    // Rapid-switch guard: if a newer call has superseded this one while the
    // siteId fetch was in flight, skip the write so the stale pair cannot
    // overwrite the newer creator's persisted state.
    if (typeof isStillCurrent === 'function' && !isStillCurrent()) {
        return null;
    }

    const stateWithSiteId = { ...state, siteId: siteId ?? null };

    setActiveCommissioningU1State(stateWithSiteId);

    return stateWithSiteId;
}

/**
 * Applies a product interaction to the active/current U1 (Rule 3).
 *
 * CSF-1791: after updating U1, fetches a fresh `siteId` from `/rakuten/site-id`
 * for the new U1 and persists `{ u1, siteId }` together as a pair. Per
 * Eduardo's dev notes (2026-05-13): "Whenever active U1 is initialized or
 * updated, fetch the matching siteId and persist `{ u1, siteId }` together",
 * and "If siteId fetch fails, skip gracefully rather than logging a new U1
 * with a stale/mismatched siteId" — on failure the prior valid pair is left
 * untouched so dependent events (ATB) skip rather than log the new U1
 * paired with the previous pair's `siteId`.
 *
 * CSF-1891: sources that do not have a product-level identifier (Bestsellers
 * carousel) pass `identifierOverride` (e.g. the literal `"bestseller"`). The
 * override is treated as active ONLY when it is a non-empty string; `null`,
 * `undefined`, and `''` are all treated as "no override" and fall through to
 * the standard `motomProductId` gate. When active, the override replaces
 * `motomProductId` as the second segment of `identifierLocation` and the
 * missing-motom early-return is skipped. Callers decide when to pass it —
 * the utility stays generic and does not know about source enum values
 * (per Eduardo's `underlying-utilities-don't-gate`).
 *
 * @param {{
 *   section?: string,
 *   identifier?: string|null,
 *   motomProductId?: string|number|null,
 *   identifierOverride?: string|null
 * }} params
 * @returns {Promise<object | null>} Updated active state with refreshed `{ u1, siteId }`, or `null` on skip/failure.
 */
export async function applyProductInteractionToActiveCommissioningU1({
    section,
    identifier = null,
    motomProductId,
    identifierOverride = null
} = {}) {
    if (!isDynamicCommissioningEnabled()) {
        return null;
    }

    const hasOverride = typeof identifierOverride === 'string' && identifierOverride.length > 0;

    if (!hasOverride && (motomProductId === null || motomProductId === undefined || motomProductId === '')) {
        Sephora.logger.verbose('CSF dynamic commissioning: skipping product-interaction U1 update — missing motomProductId');

        return null;
    }

    const active = getActiveCommissioningU1State();

    if (!active) {
        Sephora.logger.verbose('CSF dynamic commissioning: skipping product-interaction U1 update — no active state');

        return null;
    }

    const isDetailPage = Boolean(identifier) && (section === CSF_PAGE_TYPES.COLLECTIONS || section === CSF_PAGE_TYPES.POSTS);
    const uniqueKey = isDetailPage ? identifier : active.subAffiliateId;
    const pageType = getPageTypeForAttribution(section, identifier);
    const productSegment = hasOverride ? identifierOverride : motomProductId;
    const identifierLocation = `${uniqueKey}_${productSegment}`;

    /* eslint-disable camelcase */
    const u1 = constructU1Param({
        host_type: active.hostType,
        traffic_source: active.trafficSource,
        device: active.device,
        page_type: pageType,
        identifier_location: identifierLocation,
        merchant_tag: active.merchantTag,
        storefront_tag: active.storefrontTag
    });
    /* eslint-enable camelcase */

    // CSF-1791: fetch the matching siteId for the NEW u1 BEFORE persisting.
    // On failure, leave the prior `{ u1, siteId }` pair untouched so a stale
    // `siteId` is never paired with the new u1 in storage.
    const siteId = await fetchDcEventSiteIdForU1({
        linkshareSiteId: active.linkshareSiteId,
        mid: active.mid,
        u1,
        expectedSubAffiliateId: active.subAffiliateId
    });

    if (!siteId) {
        Sephora.logger.verbose('CSF dynamic commissioning: skipping product-interaction U1 update — siteId fetch failed');

        return null;
    }

    const updated = { ...active, u1, siteId };

    setActiveCommissioningU1State(updated);

    return updated;
}

/**
 * Route context for the LinkSynergy fallback builder. Callers may pass
 * `section` / `identifier` explicitly; when `section` is omitted, derive
 * from the current CSF pathname so fallback U1 matches the visible tab.
 *
 * @param {{ section?: string|null, identifier?: string|null }} params
 * @returns {{ section: string, identifier: string|null }}
 */
function resolveLinkSynergyFallbackRoute({ section = null, identifier = null } = {}) {
    if (section) {
        return { section, identifier: identifier ?? null };
    }

    if (typeof window === 'undefined') {
        return { section: CSF_PAGE_TYPES.FEATURED, identifier: identifier ?? null };
    }

    const route = getCsfRoute(window.location.pathname);

    return {
        section: route.section || CSF_PAGE_TYPES.FEATURED,
        identifier: identifier ?? route.identifier ?? null
    };
}

/**
 * Dynamic Commissioning LinkSynergy adapter ping. Parallel to the legacy
 * `initializeLinkSynergyAffiliate` in `csfAttribution.js`, but builds its
 * payload strictly from Dynamic Commissioning / Profile-API-owned data —
 * never from the legacy `CSF_ATTRIBUTION_DATA` blob.
 *
 * Payload fields (AC: prefer persisted active/current U1 when complete):
 *  - **Primary:** `u1`, `linkshareSiteId`, `mid` from {@link getActiveCommissioningU1State}.
 *  - **Fallback:** {@link buildCommissioningAttributionFieldsFromProfile} when active
 *    state is absent or incomplete (never from legacy `CSF_ATTRIBUTION_DATA`).
 *    When callers omit `section` / `identifier`, the fallback derives them from
 *    `window.location.pathname` via {@link getCsfRoute} so tab/detail U1 stays accurate.
 *  - `murl` — current page URL.
 *  - `u5` — from `CSF_DYNAMIC_COMMISSIONING_DATA` with `expectedSubAffiliateId` mismatch guard.
 *
 * CSF-1791: persists the fetched `siteID` onto active state as part of the
 * `{ u1, siteId }` pair when the ping fires against persisted active U1.
 * Returns the updated active state on persistence, or `null` on any skip
 * path (SSR, flag off, no commissioning attribution, missing required
 * fields, fetch failure, or fallback path that does not own active state).
 *
 * CSF-1773 production callers should use {@link initializeDcLinkSynergyAffiliateForActiveJourney}
 * instead so the profile+route fallback is never used when active U1 is missing.
 *
 * @param {{ creatorProfile?: object, section?: string, identifier?: string|null }} params
 * @returns {Promise<object|null>} Updated active state with `siteId` populated, or `null` on skip/failure.
 */
export async function initializeDcLinkSynergyAffiliate({ creatorProfile, section = null, identifier = null } = {}) {
    if (typeof window === 'undefined' || Sephora.isNodeRender) {
        return null;
    }

    if (!isDynamicCommissioningEnabled()) {
        return null;
    }

    const expectedSubAffiliateId = creatorProfile?.attributionDetails?.subAffiliateId ?? null;
    const active = getActiveCommissioningU1State({ expectedSubAffiliateId });

    // CSF-1791: gate on `linkshareSiteId` (Profile API input) rather than
    // `siteId` (Rakuten response). `siteId` may be null on first ping for a
    // freshly-initialized active state.
    const activePayloadReady = Boolean(active?.u1 && active.linkshareSiteId && !isMissingRequiredValue(active.mid));

    let u1;
    let linkshareSiteId;
    let mid;
    let usingActiveState = false;

    if (activePayloadReady) {
        ({ u1, linkshareSiteId, mid } = active);
        usingActiveState = true;
    } else {
        const fallbackRoute = resolveLinkSynergyFallbackRoute({ section, identifier });
        const attribution = buildCommissioningAttributionFieldsFromProfile({
            creatorProfile,
            section: fallbackRoute.section,
            identifier: fallbackRoute.identifier
        });

        if (!attribution) {
            Sephora.logger.verbose('CSF dynamic commissioning: skipping LinkSynergy ping — no commissioning attribution');

            return null;
        }

        if (!attribution.linkshareSiteId || isMissingRequiredValue(attribution.mid) || !attribution.u1) {
            Sephora.logger.verbose('CSF dynamic commissioning: skipping LinkSynergy ping — missing required fields', {
                linkshareSiteId: attribution.linkshareSiteId,
                mid: attribution.mid,
                hasU1: Boolean(attribution.u1)
            });

            return null;
        }

        ({ u1, linkshareSiteId, mid } = attribution);
    }

    const siteID = await fetchDcEventSiteIdForU1({ linkshareSiteId, mid, u1, expectedSubAffiliateId });

    if (!siteID) {
        return null;
    }

    // CSF-1791: persist `{ u1, siteId }` together onto active state so
    // subsequent in-journey events reuse the same pair (page_view / ATB
    // never refetch). Only updates persistence when the ping was fired
    // against the persisted active U1 (the fallback path has no active
    // state to attach to).
    if (usingActiveState && active) {
        const updated = { ...active, siteId: siteID };

        setActiveCommissioningU1State(updated);
        Sephora.logger.verbose('CSF dynamic commissioning: LinkSynergy ping fired, siteId persisted', { siteID, u1 });

        return updated;
    }

    Sephora.logger.verbose('CSF dynamic commissioning: LinkSynergy ping fired (fallback — no persist)', { siteID, u1 });

    return null;
}

/**
 * CSF-1773 LinkSynergy entry point: ping only when persisted active/current U1 is
 * complete for the creator. Skips entirely when active U1 is missing so Rakuten never
 * receives a profile+route fallback U1 that may diverge from entry/page_view/ATB.
 *
 * CSF-1791: gates on `linkshareSiteId` (Profile API input) and persists the
 * returned `siteID` onto active state so the `{ u1, siteId }` pair stays in
 * lockstep with the active U1 lifecycle.
 *
 * NOTE: intentionally exported with no production caller in this PR. Added in
 * CSF-1773 as the DC counterpart of the legacy `initializeLinkSynergyAffiliate`
 * and reserved for the follow-up ticket that wires the DC Rakuten ping into
 * the CSF entry / navigation flow. Do not remove without coordinating with
 * the CSF-1773 author (Eduardo) — see the CSF dynamic-commissioning isolation
 * boundary.
 *
 * @param {{ creatorProfile?: object, section?: string, identifier?: string|null }} params
 * @returns {Promise<object|null>} Updated active state with `siteId` populated, or `null` on skip/failure.
 */
export async function initializeDcLinkSynergyAffiliateForActiveJourney({ creatorProfile, section = null, identifier = null } = {}) {
    if (!isDynamicCommissioningEnabled()) {
        return null;
    }

    const expectedSubAffiliateId = creatorProfile?.attributionDetails?.subAffiliateId ?? null;
    const active = getActiveCommissioningU1State({ expectedSubAffiliateId });
    const activePayloadReady = Boolean(active?.u1 && active.linkshareSiteId && !isMissingRequiredValue(active.mid));

    if (!activePayloadReady) {
        Sephora.logger.verbose('CSF dynamic commissioning: skipping LinkSynergy ping — no persisted active U1 for journey');

        return null;
    }

    return initializeDcLinkSynergyAffiliate({ creatorProfile, section, identifier });
}

const csfMenuTabOrder = [CSF_PAGE_TYPES.FEATURED, CSF_PAGE_TYPES.PRODUCTS, CSF_PAGE_TYPES.COLLECTIONS, CSF_PAGE_TYPES.POSTS];

/** Same filter/map rules as `getFormattedMenuItems` in `withCreatorStoreFrontProps` (post-profile redirect only). */
function getFormattedMenuItemsForContentRedirect(pageType, textResources = {}, contentSummary = {}) {
    return csfMenuTabOrder
        .filter(item => {
            const { featuredCount = 0, productsCount = 0, collectionsCount = 0, postsCount = 0 } = contentSummary;

            switch (item) {
                case CSF_PAGE_TYPES.FEATURED:
                    return featuredCount > 0;
                case CSF_PAGE_TYPES.PRODUCTS:
                    return productsCount > 0;
                case CSF_PAGE_TYPES.COLLECTIONS:
                    return collectionsCount > 0;
                case CSF_PAGE_TYPES.POSTS:
                    return postsCount > 0;
                default:
                    return true;
            }
        })
        .map(item => ({
            title: textResources[item] || item,
            link: item === CSF_PAGE_TYPES.FEATURED ? '/' : `/${item.toLowerCase().replace(/\s+/g, '-')}`,
            isActive: item === pageType
        }));
}

/**
 * Prefer `profilePayload` for content counts: Redux props may not have updated in the same tick after profile init.
 *
 * @param {{ profilePayload?: object, props: object, pathname: string }} params
 * @returns {{ contentSummary: object, formattedMenuItemsForRedirect: Array<{ title: string, link: string, isActive: boolean }> }}
 */
export function getContentTabContextAfterProfileLoad({ profilePayload, props, pathname }) {
    const { creatorProfileData, creatorStoreFrontData, textResources } = props;
    const contentSummary = profilePayload?.creatorProfile?.contentSummary ?? creatorProfileData?.creatorProfile?.contentSummary ?? Empty.Object;
    const pageTypeForMenu = getPageTypeFromPath(pathname) || creatorStoreFrontData?.pageType || CSF_PAGE_TYPES.FEATURED;
    const formattedMenuItemsForRedirect = getFormattedMenuItemsForContentRedirect(pageTypeForMenu, textResources, contentSummary);

    return { contentSummary, formattedMenuItemsForRedirect };
}

/** True when referrer is another CSF page (in-journey); entry is skipped. */
function isInternalCsfReferrer(referrer) {
    if (!referrer) {
        return false;
    }

    try {
        const { hostname, pathname } = new URL(referrer);
        const normalizedPathname = pathname.replace(/^\/ca\/(?:en|fr)(?=\/)/i, '');

        return /(^|\.)sephora\./i.test(hostname.toLowerCase()) && /^\/creators(?:\/|$)/i.test(normalizedPathname);
    } catch {
        return false;
    }
}

function getOmMmcFromUrl() {
    if (typeof window === 'undefined') {
        return null;
    }

    const raw = new URLSearchParams(window.location.search).get('om_mmc');

    if (!raw || !String(raw).trim()) {
        return null;
    }

    return String(raw).trim();
}

/* eslint-disable camelcase */
function buildCsfEvent(overrides = {}) {
    return {
        id: overrides.id || UUIDv4(),
        timestamp: typeof overrides.timestamp === 'number' ? overrides.timestamp : Date.now(),
        event_type: overrides.event_type || null,
        source_url: overrides.source_url ?? null,
        destination_url: overrides.destination_url ?? null,
        creator_id: overrides.creator_id ?? null,
        u1: overrides.u1 ?? null,
        u5: overrides.u5 ?? null,
        siteId: overrides.siteId ?? null,
        navigation_type: overrides.navigation_type ?? null,
        om_mmc: overrides.om_mmc ?? null,
        product_id: overrides.product_id ?? null,
        motom_product_id: overrides.motom_product_id ?? null,
        search_term: overrides.search_term ?? null
    };
}

function buildCsfJourney(entryEvent) {
    const entryTimestamp = entryEvent?.timestamp ?? Date.now();

    return {
        journey_id: UUIDv4(),
        entry_timestamp: entryTimestamp,
        expires_at: entryTimestamp + CSF_JOURNEY_TTL_MS,
        events: entryEvent ? [entryEvent] : []
    };
}
/* eslint-enable camelcase */

// ──────────────────────────────────────────────────────────────────────────────
// Serialized writer for `csf_journeys`.
//
// Journey writes are read-modify-write. Multiple rapid SPA navigations
// (e.g. quick tab taps) issue overlapping writes; without serialization the
// later setItem clobbers events appended by the earlier one. Each task is
// chained through a single module-level promise so writes are strictly
// ordered. A task's failure does NOT break the chain for subsequent tasks.
// ──────────────────────────────────────────────────────────────────────────────

let journeysWriteQueue = Promise.resolve();

/**
 * Enqueue an async read-modify-write task against `csf_journeys`. Tasks run
 * strictly in submission order; one task's rejection is isolated from the
 * tail of the queue.
 *
 * @template T
 * @param {() => Promise<T>} task
 * @returns {Promise<T>}
 */
function enqueueJourneysWrite(task) {
    const next = journeysWriteQueue.then(task, task);

    journeysWriteQueue = next.catch(() => {});

    return next;
}

function getMissingRequiredEntryFields({
    destinationUrl,
    creatorId,
    u1,
    siteId,
    productId = null,
    includeDestinationUrl = true,
    includeAtbFields = false
}) {
    const missing = [];

    if (includeDestinationUrl && !destinationUrl) {
        missing.push('destination_url');
    }

    if (isMissingRequiredValue(creatorId)) {
        missing.push('creator_id');
    }

    if (isMissingRequiredValue(u1)) {
        missing.push('u1');
    }

    if (isMissingRequiredValue(siteId)) {
        missing.push('siteId');
    }

    // CSF-1797 (Eduardo, 2026-05-18): `motom_product_id` is OPTIONAL for ATB
    // events. Constructor.io-fed carousels (e.g. Bestsellers on CSF pages) do
    // not provide the field, so requiring it suppresses legitimate add_to_cart
    // logging. Only `product_id` remains a hard requirement.
    if (includeAtbFields && isMissingRequiredValue(productId)) {
        missing.push('product_id');
    }

    return missing;
}

/**
 * Resets the CSF Dynamic Commissioning journey storage and logs a fresh `entry`
 * event as the only event of the new active journey.
 *
 * Per CSF-1699 dev notes (Gutierrez 2026-05-11), an `entry` event is logged
 * only when the user lands on a CSF page as a new external entry point. In
 * that case the previous IndexedDB journeys are cleared and replaced with a
 * single new journey that contains exactly this entry event.
 *
 * The reset is intentionally gated by the same guards used for entry-event
 * validity:
 *   - flag off → no-op (no clear, no log);
 *   - SSR / Storage.db unavailable → no-op;
 *   - internal CSF referrer (another `/creators/...` page, e.g. in-journey tab
 *     or SPA navigation within CSF) → no-op; other Sephora referrers
 *     (e.g. PDP → CSF) may log a new entry;
 *   - missing required entry fields (u1, siteId, creator_id, destination_url)
 *     → no-op (Eduardo: "If a valid entry event cannot be built/logged, do
 *     not clear the existing IndexedDB journey").
 *
 * Legacy `csfAttributionData` and `affiliateGatewayParameters` localStorage
 * keys are NOT touched — only the IndexedDB `csf_journeys` container is
 * overwritten.
 *
 * @param {{ destinationUrl?: string, creatorProfile?: object, section?: string, identifier?: string|null, u5?: string|null }} [params]
 * @returns {Promise<void>}
 */
export async function logCsfEntryInComponent({
    destinationUrl, creatorProfile, section, identifier = null, u5 = null
} = {}) {
    if (!isDynamicCommissioningEnabled()) {
        return;
    }

    // SSR guard — `Storage.db` in node renders is built from `errorMethods(...)`
    // (functions that throw), so the typeof check below is not enough.
    if (Sephora.isNodeRender) {
        return;
    }

    if (typeof Storage.db?.setItem !== 'function') {
        return;
    }

    const referrer = (typeof document !== 'undefined' && document.referrer) || '';

    if (referrer && isInternalCsfReferrer(referrer)) {
        return;
    }

    const builtState = buildActiveCommissioningU1StateFromProfile({ creatorProfile, section, identifier });

    const sourceUrl = referrer ? referrer : null;
    const omMmc = getOmMmcFromUrl();

    const resolvedCreatorId = creatorProfile?.creatorId ?? null;
    const u1 = builtState?.u1 ?? null;

    // CSF-1791: validate param-only inputs BEFORE firing the `/rakuten/site-id`
    // ping so a missing `destinationUrl` / `creator_id` / `u1` (entry will be
    // skipped anyway) does not trigger an unnecessary network call.
    const preFetchMissing = getMissingRequiredEntryFields({
        destinationUrl,
        creatorId: resolvedCreatorId,
        u1,
        siteId: 'pending',
        includeDestinationUrl: true,
        includeAtbFields: false
    });

    if (preFetchMissing.length) {
        Sephora.logger.verbose('CSF dynamic commissioning: skipping entry, missing fields', preFetchMissing);

        return;
    }

    // CSF-1791: fetch the matching `siteId` from `/rakuten/site-id` BEFORE
    // logging entry. The Creator Profile `linkshareSiteId` is the ping
    // INPUT — not the event `siteId`. Per Eduardo's dev notes (2026-05-13):
    // "Entry/product events should only log after the matching siteId is
    // available", and "If siteId fetch fails, skip gracefully rather than
    // logging a new U1 with a stale/mismatched siteId".
    const siteId = await fetchDcEventSiteIdForU1({
        linkshareSiteId: builtState.linkshareSiteId,
        mid: builtState.mid,
        u1: builtState.u1,
        expectedSubAffiliateId: builtState.subAffiliateId
    });

    if (isMissingRequiredValue(siteId)) {
        Sephora.logger.verbose('CSF dynamic commissioning: skipping entry, missing fields', ['siteId']);

        return;
    }

    const stateWithSiteId = { ...builtState, siteId };

    /* eslint-disable camelcase */
    const event = buildCsfEvent({
        event_type: CSF_EVENT_TYPES.ENTRY,
        source_url: sourceUrl,
        destination_url: destinationUrl,
        creator_id: resolvedCreatorId,
        u1,
        u5,
        siteId,
        om_mmc: omMmc
    });
    /* eslint-enable camelcase */

    // CSF-1699: overwrite the journeys container with a single fresh journey
    // containing only this entry event. This clear+write runs through the
    // serialized journeys writer, so other queued journey writes (for example
    // page_view appends that also use the queue) cannot interleave between the
    // wipe and the new journey landing. Direct Storage.db writers are not
    // serialized by this call alone.
    await enqueueJourneysWrite(async () => {
        try {
            await Storage.db.setItem(CSF_JOURNEYS_KEY, { journeys: [buildCsfJourney(event)] }, null, Tables.CsfAttribution);

            // CSF-1773 + CSF-1791: persist `{ u1, siteId }` pair only after
            // a successful entry write. The persisted siteId here is the
            // value returned by `/rakuten/site-id`, not the Profile API
            // `linkshareSiteId`.
            setActiveCommissioningU1State(stateWithSiteId);
        } catch (err) {
            Sephora.logger.error('CSF dynamic commissioning: failed to track entry event to IndexedDB', err);
        }
    });
}

function journeyContainsEntryEvent(journey) {
    const events = Array.isArray(journey?.events) ? journey.events : [];

    return events.some(e => e?.event_type === CSF_EVENT_TYPES.ENTRY);
}

/**
 * Persists CSF add-to-basket events (tile and quicklook) when dynamic commissioning is enabled.
 * Appends only to an existing journey that already has an **`entry`** event; does not create
 * journeys (that is reserved for `logCsfEntryInComponent`).
 *
 * @param {{
 *   eventType?: string,
 *   creatorId?: number|null,
 *   u1?: string|null,
 *   u5?: string|null,
 *   siteId?: string|null,
 *   productId?: string|null,
 *   motomProductId?: string|null
 * }} [params]
 * @returns {Promise<void>}
 */
export async function logCsfAtbEvent({
    eventType = CSF_EVENT_TYPES.ADD_TO_CART,
    creatorId = null,
    u1 = null,
    u5 = null,
    siteId = null,
    productId = null,
    motomProductId = null
} = {}) {
    if (!isDynamicCommissioningEnabled()) {
        return;
    }

    if (typeof Storage.db?.getItem !== 'function' || typeof Storage.db?.setItem !== 'function') {
        return;
    }

    const missing = getMissingRequiredEntryFields({
        creatorId,
        u1,
        siteId,
        productId,
        includeDestinationUrl: false,
        includeAtbFields: true
    });

    if (missing.length) {
        Sephora.logger.verbose('CSF dynamic commissioning: skipping ATB event, missing fields', missing);

        return;
    }

    const resolvedEventType = eventType === CSF_EVENT_TYPES.QUICKLOOK_ATB ? CSF_EVENT_TYPES.QUICKLOOK_ATB : CSF_EVENT_TYPES.ADD_TO_CART;
    /* eslint-disable camelcase */
    const event = buildCsfEvent({
        event_type: resolvedEventType,
        creator_id: creatorId,
        u1,
        u5,
        siteId,
        product_id: productId,
        motom_product_id: motomProductId
    });
    /* eslint-enable camelcase */

    try {
        const existing = await Storage.db.getItem(CSF_JOURNEYS_KEY, true, false, false, Tables.CsfAttribution);
        const journeys = Array.isArray(existing?.journeys) ? existing.journeys : [];

        if (!journeys.length) {
            Sephora.logger.verbose('CSF dynamic commissioning: skipping ATB event, no journeys (entry event creates journeys)');

            return;
        }

        let targetIdx = -1;

        for (let i = journeys.length - 1; i >= 0; i--) {
            if (journeyContainsEntryEvent(journeys[i])) {
                targetIdx = i;

                break;
            }
        }

        if (targetIdx === -1) {
            Sephora.logger.verbose('CSF dynamic commissioning: skipping ATB event, no journey contains an entry event');

            return;
        }

        const targetJourney = journeys[targetIdx] || {};
        const targetJourneyEvents = Array.isArray(targetJourney.events) ? targetJourney.events : [];
        // CSF-1891 (Eduardo, 2026-06-30): stamp the parent journey_id onto the
        // event before append so add_to_cart carries the same journey reference
        // page_view already writes (see logCsfPageViewInComponent).
        /* eslint-disable camelcase */
        const eventWithJourneyId = { ...event, journey_id: targetJourney.journey_id };
        /* eslint-enable camelcase */
        const updatedJourney = {
            ...targetJourney,
            events: [...targetJourneyEvents, eventWithJourneyId]
        };
        const updatedJourneys = [...journeys];
        updatedJourneys[targetIdx] = updatedJourney;

        await Storage.db.setItem(CSF_JOURNEYS_KEY, { journeys: updatedJourneys }, null, Tables.CsfAttribution);
    } catch (err) {
        Sephora.logger.error('CSF dynamic commissioning: failed to track ATB event to IndexedDB', err);
    }
}

/**
 * Removes expired journeys from the IndexedDB csf_attribution table.
 * A journey is expired when its expires_at timestamp is less than or equal to Date.now().
 *
 * Designed to run on:
 * - CSF page load (fire-and-forget)
 * - Checkout before order submission (awaited)
 *
 * @returns {Promise<void>}
 */
export async function cleanExpiredJourneys() {
    if (!isDynamicCommissioningEnabled()) {
        return;
    }

    // SSR guard — `Storage.db` in node renders is built from `errorMethods(...)`
    // (functions that throw), so the typeof check below is not enough.
    if (Sephora.isNodeRender) {
        return;
    }

    if (typeof Storage.db?.getItem !== 'function' || typeof Storage.db?.setItem !== 'function' || typeof Storage.db?.removeItem !== 'function') {
        return;
    }

    await enqueueJourneysWrite(async () => {
        try {
            const data = await Storage.db.getItem(CSF_JOURNEYS_KEY, true, false, false, Tables.CsfAttribution);
            const journeys = Array.isArray(data?.journeys) ? data.journeys : null;

            if (!journeys?.length) {
                return;
            }

            const now = Date.now();
            const validJourneys = journeys.filter(journey => journey['expires_at'] > now);

            // Nothing expired — no write needed
            if (validJourneys.length === journeys.length) {
                return;
            }

            if (validJourneys.length === 0) {
                await Storage.db.removeItem(CSF_JOURNEYS_KEY, Tables.CsfAttribution);
                // No active journeys means any persisted u5 / active U1 is stale.
                clearCsfDynamicCommissioningU5();
                clearActiveCommissioningU1State();
            } else {
                await Storage.db.setItem(CSF_JOURNEYS_KEY, { ...data, journeys: validJourneys }, null, Tables.CsfAttribution);
            }
        } catch (err) {
            Sephora.logger.error('CSF dynamic commissioning: failed to clean expired journeys from IndexedDB', err);
        }
    });
}

/**
 * Shared checkout place-order flow for CSF dynamic commissioning affiliate fields.
 * Cleans expired journeys (best-effort), resolves checkout commissioning, then builds
 * `linkshareInformation` for `checkoutApi.placeOrder`. Used by both RWD and Frictionless
 * checkout place-order buttons.
 *
 * BOPIS: pass `linkshareInformation` (with `ruleId` when DC wins) on `submitOrder` only; sync
 * legacy `affiliateGatewayParameters` after success via {@link syncBopisDcAttributionAfterPlaceOrderSuccess}.
 *
 * @param {{ getAffiliateGatewayParams: () => { linkshareSiteId?: string, linkshareTime?: string } }} params
 * @returns {Promise<{ linkshareSiteId: string, linkshareTime: string, ruleId: string }|{ linkshareSiteId: string, linkshareTime: string }|null>}
 *   Same return shapes as {@link buildPlaceOrderLinkshareInformation}; non-null values always include `linkshareTime`.
 */
export async function prepareCheckoutPlaceOrderLinkshareInformation({ getAffiliateGatewayParams }) {
    await cleanExpiredJourneys().catch(() => {});

    const checkoutCommissioning = await resolveCheckoutDynamicCommissioning().catch(err => {
        Sephora.logger.error('CSF dynamic commissioning: checkout resolution failed', err);

        return null;
    });

    let affiliateGatewayParams = {};

    try {
        affiliateGatewayParams = typeof getAffiliateGatewayParams === 'function' ? getAffiliateGatewayParams() : {};
    } catch (err) {
        Sephora.logger.error('CSF dynamic commissioning: failed to read affiliate gateway params for checkout', err);
    }

    return buildPlaceOrderLinkshareInformation(checkoutCommissioning, affiliateGatewayParams);
}

// ──────────────────────────────────────────────────────────────────────────────
// History-state navigation index (back/forward detection for CSF-1686)
//
// PopStateEvent does not natively expose direction. We stamp each pushed
// history entry with a monotonic `csfNavIndex`; on popstate, comparing the
// entry's index to the last seen index yields back/forward/none.
//
// The counter MUST be monotonic across the entire document session — older
// CSF entries higher up in the history stack may still be reachable via
// browser back even after the user left CSF and re-entered. We never
// decrease the counter, and persist it in `sessionStorage` so a page reload
// (which preserves history but resets module state) cannot misclassify a
// back into a still-stamped entry as forward.
// ──────────────────────────────────────────────────────────────────────────────

const CSF_NAV_INDEX_SESSION_KEY = 'csf_dc_nav_index';

let lastSeenCsfNavIndex = 0;

function readPersistedNavIndex() {
    try {
        const raw = window.sessionStorage?.getItem(CSF_NAV_INDEX_SESSION_KEY);
        const parsed = raw == null ? NaN : Number(raw);

        return Number.isFinite(parsed) ? parsed : null;
    } catch {
        return null;
    }
}

function writePersistedNavIndex(value) {
    try {
        // Persistence stores the max counter ever seen — never decreases — so
        // the monotonic guarantee from the header comment holds even when the
        // in-memory cursor moves backwards (e.g. browser back).
        const current = readPersistedNavIndex();
        const monotonic = Math.max(Number.isFinite(current) ? current : 0, value);

        window.sessionStorage?.setItem(CSF_NAV_INDEX_SESSION_KEY, String(monotonic));
    } catch {
        // sessionStorage unavailable / quota — best-effort persistence only.
    }
}

function setLastSeenCsfNavIndex(value) {
    lastSeenCsfNavIndex = value;
    writePersistedNavIndex(value);
}

/**
 * Initializes the nav-index counter for the current document. If the current
 * history entry has no `csfNavIndex` stamp, it is stamped with the next
 * monotonic index (so it cannot collide with a previously-stamped entry that
 * is still reachable higher in the history stack). The counter never
 * decreases. Safe to call multiple times.
 */
export function initCsfNavIndex() {
    if (typeof window === 'undefined') {
        return;
    }

    const currentState = window.history.state;
    const stateIndex = currentState?.csfNavIndex;
    const persistedIndex = readPersistedNavIndex();

    // Take the highest known counter — never go backwards when generating a
    // new stamp. We trust the ground-truth sources (history.state and
    // sessionStorage) over the in-memory cache so a fresh document (page
    // reload, new module instance) still recovers the correct monotonic
    // baseline for future entries.
    const baseline = Math.max(Number.isFinite(stateIndex) ? stateIndex : 0, Number.isFinite(persistedIndex) ? persistedIndex : 0);

    if (Number.isFinite(stateIndex)) {
        // When the current history entry is already stamped, restore the
        // current position from history.state rather than the persisted max.
        // This keeps back/forward direction detection aligned with the actual
        // entry the user reloaded on.
        setLastSeenCsfNavIndex(stateIndex);

        return;
    }

    // Un-stamped entry (e.g. arrived via a foreign SPA router that did not
    // call `tagPushedHistoryState`). Stamp it with `baseline + 1` to keep the
    // counter strictly monotonic and avoid colliding with any previously
    // stamped entry still reachable in the history stack.
    const nextIndex = baseline + 1;

    window.history.replaceState({ ...(currentState || {}), csfNavIndex: nextIndex }, '');
    setLastSeenCsfNavIndex(nextIndex);
}

/**
 * Stamps the just-pushed history entry with the next monotonic `csfNavIndex`.
 * Must be called immediately after `window.history.pushState(...)` inside
 * `csfNavigation.navigateTo`.
 */
export function tagPushedHistoryState() {
    if (typeof window === 'undefined') {
        return;
    }

    // Bump from the highest known counter (persisted max OR in-memory cursor)
    // so a new stamp cannot collide with an older entry still reachable in the
    // history stack — the in-memory cursor may be lower after browser back.
    const persistedIndex = readPersistedNavIndex();
    const baseline = Math.max(lastSeenCsfNavIndex, Number.isFinite(persistedIndex) ? persistedIndex : 0);
    const nextIndex = baseline + 1;
    const currentState = window.history.state;

    window.history.replaceState({ ...(currentState || {}), csfNavIndex: nextIndex }, '');
    setLastSeenCsfNavIndex(nextIndex);
}

export function isRealBrowserHistoryNavigation(event) {
    if (event?.isTrusted === true) {
        return true;
    }

    return Number.isFinite(event?.state?.csfNavIndex);
}

/**
 * Resolves the navigation direction for a popstate event by comparing the
 * popped entry's `csfNavIndex` stamp against the in-memory last-seen index.
 *
 * Side effect: when a finite stamp is found, advances/rewinds the module-level
 * last-seen cursor and persists the monotonic max to `sessionStorage` (via
 * `setLastSeenCsfNavIndex`).
 *
 * @param {PopStateEvent | { state?: { csfNavIndex?: number } }} event
 *        Popstate-shaped object; only `event.state.csfNavIndex` is read.
 * @returns {'back' | 'forward' | null}
 *        `'back'` / `'forward'` for stamped browser history navigation;
 *        `null` when the stamp is missing or equal to the last-seen index
 *        (e.g. synthetic popstate without a `state` payload).
 */
export function getNavigationTypeFromPopState(event) {
    const stateIndex = event?.state?.csfNavIndex;

    if (!Number.isFinite(stateIndex)) {
        return null;
    }

    if (stateIndex < lastSeenCsfNavIndex) {
        setLastSeenCsfNavIndex(stateIndex);

        return 'back';
    }

    if (stateIndex > lastSeenCsfNavIndex) {
        setLastSeenCsfNavIndex(stateIndex);

        return 'forward';
    }

    return null;
}

/**
 * Allowed values for `navigation_type` per the CSF dynamic commissioning event
 * schema. Anything outside this whitelist is treated as missing/invalid.
 */
const ALLOWED_NAVIGATION_TYPES = new Set([null, 'back', 'forward']);

/**
 * Required value-fields for a `page_view` event per CSF-1686 AC.
 * `om_mmc` is part of the schema (always set; `null` for non-entry events per
 * the ticket's Technical Details table) and is therefore not gated here.
 */
function getMissingRequiredPageViewFields({ creatorId, u1, siteId, navigationType }) {
    const missing = [];

    if (!creatorId) {
        missing.push('creator_id');
    }

    if (!u1) {
        missing.push('u1');
    }

    if (!siteId) {
        missing.push('siteId');
    }

    if (!ALLOWED_NAVIGATION_TYPES.has(navigationType)) {
        missing.push('navigation_type');
    }

    return missing;
}

/**
 * Appends an event to the most recent non-expired journey in the
 * `csf_attribution` IndexedDB store. No-op when there is no active journey
 * (per CSF-1686: page_view outside a journey is dropped).
 *
 * @param {object} event Pre-built CSF event.
 * @returns {Promise<void>}
 */
async function appendEventToActiveJourney(event) {
    // SSR guard — `Storage.db` in node renders is built from `errorMethods(...)`
    // (functions that throw), so the typeof check below is not enough.
    if (Sephora.isNodeRender) {
        return;
    }

    if (typeof Storage.db?.getItem !== 'function' || typeof Storage.db?.setItem !== 'function') {
        return;
    }

    await enqueueJourneysWrite(async () => {
        try {
            const existing = await Storage.db.getItem(CSF_JOURNEYS_KEY, true, false, false, Tables.CsfAttribution);
            const journeys = Array.isArray(existing?.journeys) ? existing.journeys : [];

            if (!journeys.length) {
                return;
            }

            const now = Date.now();
            let activeIdx = -1;

            for (let i = journeys.length - 1; i >= 0; i--) {
                if (journeys[i]?.['expires_at'] > now) {
                    activeIdx = i;

                    break;
                }
            }

            if (activeIdx === -1) {
                return;
            }

            const activeJourney = journeys[activeIdx];
            /* eslint-disable camelcase */
            const eventWithJourneyId = { ...event, journey_id: activeJourney.journey_id };
            /* eslint-enable camelcase */

            const existingEvents = Array.isArray(activeJourney.events) ? activeJourney.events : [];
            const updatedJourney = {
                ...activeJourney,
                events: [...existingEvents, eventWithJourneyId]
            };
            const updatedJourneys = journeys.slice();
            updatedJourneys[activeIdx] = updatedJourney;

            await Storage.db.setItem(CSF_JOURNEYS_KEY, { ...existing, journeys: updatedJourneys }, null, Tables.CsfAttribution);
        } catch (err) {
            Sephora.logger.error('CSF dynamic commissioning: failed to track page_view event to IndexedDB', err);
        }
    });
}

/**
 * Persists a CSF `page_view` event to the active journey when dynamic
 * commissioning is enabled. No-ops when the feature flag is off, required
 * value-fields are missing, or no active journey exists.
 *
 * @param {{
 *   destinationUrl?: string,
 *   creatorId?: number|null,
 *   u1?: string|null,
 *   siteId?: string|null,
 *   u5?: string|null,
 *   navigationType: 'back'|'forward'|null,
 *   sourceUrl?: string|null
 * }} [params]
 * @returns {Promise<void>}
 */
export async function logCsfPageViewInComponent({
    destinationUrl,
    creatorId = null,
    u1 = null,
    siteId = null,
    u5 = null,
    navigationType,
    sourceUrl = null
} = {}) {
    if (!isDynamicCommissioningEnabled()) {
        return;
    }

    if (typeof window === 'undefined') {
        return;
    }

    const missing = getMissingRequiredPageViewFields({ creatorId, u1, siteId, navigationType });

    if (missing.length) {
        Sephora.logger.verbose('CSF dynamic commissioning: skipping page_view, missing fields', missing);

        return;
    }

    /* eslint-disable camelcase */
    const event = buildCsfEvent({
        event_type: CSF_EVENT_TYPES.PAGE_VIEW,
        source_url: sourceUrl,
        destination_url: destinationUrl ?? null,
        creator_id: creatorId,
        u1,
        u5,
        siteId,
        navigation_type: navigationType ?? null,
        om_mmc: null
    });
    /* eslint-enable camelcase */

    await appendEventToActiveJourney(event);
}
